import Dialog from '@/global/guides/Dialog'
import { cn } from '@/lib/utils'
import axios from 'axios'
import { DownloadIcon, ExternalLink, UnlinkIcon, XIcon } from 'lucide-react'
import IconButton from '../Button/IconButton'

interface IProps {
    image?: string | null
    open: boolean
    onClose: () => void
}

export function DialogFile({ image, open, onClose }: IProps) {
    // Função utilitária para obter a extensão do arquivo
    function getFileExtension(filename?: string | null) {
        if (!filename) return ''
        return filename.split('.').pop()?.toLowerCase() || ''
    }

    const extension = getFileExtension(image)
    const url = `${import.meta.env.VITE_BASE_URL}/v1/uploads/${image}`

    const handleClick = async () => {
        await axios
            .get(url, {
                responseType: 'blob',
            })
            .then(response => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data]),
                )
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', image!)
                document.body.appendChild(link)
                link.click()
            })
    }

    // Função para renderizar o conteúdo conforme o tipo do arquivo
    function renderFileContent() {
        if (!image) return null

        if (
            ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
                extension,
            )
        ) {
            return (
                <img
                    src={url}
                    alt="dialog-file"
                    style={{ width: '100%', height: 'auto', maxHeight: '90%' }}
                />
            )
        }

        if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(extension)) {
            return (
                <div className="w-full flex justify-center">
                    <video
                        controls
                        style={{ maxWidth: '100%', maxHeight: 400 }}
                    >
                        <source src={url} type={`video/${extension}`} />
                        Seu navegador não suporta o elemento de vídeo.
                    </video>
                </div>
            )
        }

        return (
            <div
                className={cn(
                    'bg-card rounded-lg size-fit p-4 max-w-screen',
                    'flex items-center justify-center gap-4 select-none',
                )}
            >
                <UnlinkIcon className="size-6" />
                <span className="text-xl">
                    Pré-visualização não suportada para este tipo de arquivo.
                </span>
            </div>
        )
    }

    return (
        <Dialog.Root
            open={open}
            onClose={onClose}
            className={cn(
                'max-w-[90vw] max-h-[90vh] size-fit',
                'pt-4 flex items-center justify-center',
                'min-w-[10rem] mx-auto my-auto',
            )}
            PaperProps={{ sx: { background: 'transparent' } }}
        >
            <div className="relative pt-8">
                <div
                    className={cn(
                        'absolute top-0 right-0 z-2',
                        '*:bg-white *:text-gray-600',
                        '*:hover:bg-gray-200 *:hover:text-gray-800 transition-colors',
                        '*:border *:text-base',
                        'flex items-center gap-2 p-1',
                        'firefox-blur bg-white/20',
                        '*size-8',
                        '*:rounded-full',
                        'rounded-full',
                        '*:gap-2',
                    )}
                >
                    <IconButton
                        onClick={() => {
                            window.open(url, '_blank', 'noopener,noreferrer')
                        }}
                        title="Abrir em nova aba"
                    >
                        <ExternalLink />
                    </IconButton>
                    <IconButton onClick={handleClick} title="Baixar arquivo">
                        <DownloadIcon />
                    </IconButton>
                    <IconButton onClick={onClose} title="Fechar">
                        <XIcon className="size-6" />
                    </IconButton>
                </div>
                <div
                    className={cn(
                        'flex flex-col items-center justify-center',
                        'bg-transparent size-full max-w-full max-h-full',
                        'overflow-auto',
                    )}
                >
                    {renderFileContent()}
                </div>
            </div>
        </Dialog.Root>
    )
}
